import { CircularProgress } from "@material-ui/core";
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useEffect, useState } from "react";
import {
  LuArrowRight,
  LuChevronRight,
  LuFileText,
  LuInfo,
  LuPackage,
  LuSettings2,
  LuShip,
  LuZap,
} from "react-icons/lu";

export const T = {
  dark: "#081f35",
  primary: "#313C4C",
  light: "#dfe6ed",
  bg: "#f8fafc",
};

export const C = {
  teal: {
    bg: "#E1F5EE",
    border: "#0F6E56",
    text: "#085041",
    dot: "#1D9E75",
    icon: "#0F6E56",
  },
  purple: {
    bg: "#EEEDFE",
    border: "#534AB7",
    text: "#3C3489",
    dot: "#7F77DD",
    icon: "#534AB7",
  },
  coral: {
    bg: "#FAECE7",
    border: "#993C1D",
    text: "#712B13",
    dot: "#D85A30",
    icon: "#993C1D",
  },
  amber: {
    bg: "#FAEEDA",
    border: "#854F0B",
    text: "#633806",
    dot: "#BA7517",
    icon: "#854F0B",
  },
  gray: {
    bg: "#F1EFE8",
    border: "#5F5E5A",
    text: "#2C2C2A",
    dot: "#888780",
    icon: "#5F5E5A",
  },
  green: {
    bg: "#EAF3DE",
    border: "#3B6D11",
    text: "#27500A",
    dot: "#639922",
    icon: "#3B6D11",
  },
};

const TARGET_HANDLE = {
  position: "absolute",
  left: "50%",
  top: 0,
  transform: "translate(-50%, -50%)",
  opacity: 0,
  pointerEvents: "none",
};

const SOURCE_HANDLE = {
  position: "absolute",
  left: "50%",
  bottom: 0,
  top: "auto",
  transform: "translate(-50%, 50%)",
  opacity: 0,
  pointerEvents: "none",
};

const ICON_MAP = {
  "Create Cargo": LuPackage,
  "Create Vessel": LuShip,
  "Cargo Contract Tab": LuFileText,
  "Vessel Contract Tab": LuFileText,
  "Pre-Fix Estimate": LuFileText,
  "Operation Tab": LuSettings2,
};

const BADGE_MAP = {
  "Create Cargo": { label: "Resource", color: C.teal },
  "Create Vessel": { label: "Resource", color: C.coral },
  "Cargo Contract Tab": { label: "Contract", color: C.teal },
  "Vessel Contract Tab": { label: "Contract", color: C.coral },
  "Pre-Fix Estimate": { label: "Estimate", color: C.amber },
  "Operation Tab": { label: "Execution", color: C.green },
};

const STEP_MAP = {
  "Create Cargo": "01",
  "Create Vessel": "02",
  "Cargo Contract Tab": "03",
  "Vessel Contract Tab": "03",
  "Pre-Fix Estimate": "04",
  "Operation Tab": "05",
};

const ClickHereLink = ({ accent, hov, small = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 4,
      marginTop: small ? 6 : 8,
      fontSize: small ? 7.5 : 8,
      fontWeight: 700,
      color: hov ? accent : "#94a3b8",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      transition: "color 0.2s ease",
      cursor: "pointer",
    }}
  >
    <span>Click here</span>
    <LuArrowRight
      size={small ? 7 : 8}
      color={hov ? accent : "#94a3b8"}
      strokeWidth={2.5}
      style={{ transition: "color 0.2s ease", flexShrink: 0 }}
    />
  </div>
);

export const GroupNode = ({ data }) => {
  const [hov, setHov] = useState(false);
  const accent = data.color || T.primary;
  const IconComp = ICON_MAP[data.title];
  const badge = BADGE_MAP[data.title];
  const stepNum = STEP_MAP[data.title];

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: data.width,
        minHeight: data.height,
        border: "1.5px solid #e2e8f0",
        borderTop: `3px solid ${accent}`,
        borderRadius: 16,
        boxShadow:
          hov && data.route
            ? `0 0 0 1.5px ${accent}, 0 16px 32px -10px ${accent}30, 0 2px 10px -3px ${accent}14`
            : "0 2px 14px -4px rgba(0,0,0,0.07)",
        background:
          hov && data.route
            ? "#ffffff"
            : "linear-gradient(155deg,#ffffff 0%,#f4f8fc 100%)",
        padding: "14px 16px 15px",
        boxSizing: "border-box",
        position: "relative",
        cursor: data.route ? "pointer" : "default",
        transition:
          "box-shadow 0.22s ease, background 0.22s ease, transform 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        transform:
          hov && data.route
            ? "translateY(-4px) scale(1.01)"
            : "translateY(0) scale(1)",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        overflow: "hidden",
      }}
    >
      <Handle type="target" position={Position.Top} style={TARGET_HANDLE} />

      {stepNum && (
        <span
          style={{
            position: "absolute",
            bottom: -4,
            right: 8,
            fontSize: 52,
            fontWeight: 900,
            color: `${accent}08`,
            lineHeight: 1,
            letterSpacing: "-2px",
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          {stepNum}
        </span>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              background: `linear-gradient(135deg,${accent}18 0%,${accent}0c 100%)`,
              border: `1px solid ${accent}20`,
              borderRadius: 10,
              flexShrink: 0,
              boxShadow: `0 2px 6px -1px ${accent}16`,
            }}
          >
            {IconComp && (
              <IconComp size={16} color={accent} strokeWidth={2.2} />
            )}
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: T.dark,
              letterSpacing: "-0.1px",
              lineHeight: 1.2,
            }}
          >
            {data.title}
          </span>
        </div>

        {badge && (
          <span
            style={{
              fontSize: 7.5,
              fontWeight: 800,
              color: badge.color.text,
              background: badge.color.bg,
              border: `1px solid ${badge.color.border}26`,
              padding: "2px 7px",
              borderRadius: 20,
              letterSpacing: "0.7px",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {badge.label}
          </span>
        )}
      </div>

      <div
        style={{
          width: "100%",
          height: 1,
          background: `linear-gradient(90deg,${accent}26 0%,transparent 100%)`,
          marginBottom: 8,
        }}
      />

      {data.sub && (
        <p
          style={{
            fontSize: 9.5,
            color: "#4a6080",
            margin: 0,
            fontWeight: 500,
            lineHeight: 1.6,
            paddingRight: 4,
          }}
        >
          {data.sub}
        </p>
      )}

      {data.route && <ClickHereLink accent={accent} hov={hov} />}

      <Handle type="source" position={Position.Bottom} style={SOURCE_HANDLE} />
    </div>
  );
};

export const CardNode = ({ data }) => {
  const [hov, setHov] = useState(false);
  const color = data.color || {
    bg: "#fff",
    border: "#cbd5e1",
    text: T.dark,
    dot: T.primary,
    icon: T.primary,
  };

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: data.width,
        border: "1.5px solid #e8edf3",
        borderBottom: "2.5px solid #e8edf3",
        borderRadius: 12,
        boxShadow:
          hov && data.route
            ? `0 0 0 1.5px ${color.border}, 0 10px 22px -7px ${color.dot}28`
            : "0 1px 7px -1px rgba(0,0,0,0.05)",
        background:
          hov && data.route
            ? `linear-gradient(145deg,${color.bg} 0%,#ffffff 100%)`
            : "#ffffff",
        padding: "11px 13px 10px",
        textAlign: "center",
        cursor: data.route ? "pointer" : "default",
        transition:
          "box-shadow 0.22s ease, background 0.22s ease, transform 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        transform:
          hov && data.route
            ? "translateY(-4px) scale(1.02)"
            : "translateY(0) scale(1)",
        boxSizing: "border-box",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0,
      }}
    >
      <Handle type="target" position={Position.Top} style={TARGET_HANDLE} />

      <div
        style={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 28,
          height: 2.5,
          borderRadius: "0 0 4px 4px",
          background: color.dot,
          opacity: hov ? 0.95 : 0.5,
          transition: "opacity 0.2s ease",
        }}
      />

      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: `radial-gradient(circle at 35% 35%,${color.dot}ee,${color.border})`,
          display: "block",
          marginBottom: 6,
          marginTop: 6,
          boxShadow: `0 1px 4px ${color.dot}50`,
        }}
      />

      <div
        style={{
          fontSize: 10.5,
          fontWeight: 800,
          color: color.text,
          lineHeight: 1.25,
          letterSpacing: "-0.1px",
        }}
      >
        {data.title}
      </div>

      {data.sub && (
        <div
          style={{
            fontSize: 8.5,
            color: color.text,
            opacity: 0.6,
            marginTop: 4,
            fontWeight: 500,
            lineHeight: 1.45,
          }}
        >
          {data.sub}
        </div>
      )}

      {data.route && <ClickHereLink accent={color.border} hov={hov} small />}

      <Handle type="source" position={Position.Bottom} style={SOURCE_HANDLE} />
    </div>
  );
};

export const PillNode = ({ data }) => (
  <div
    style={{
      width: data.width,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,
      fontSize: 7.5,
      fontWeight: 800,
      color: "#3730a3",
      background: "linear-gradient(135deg,#f0f0ff 0%,#e0e7ff 100%)",
      border: "1px solid #c7d2fe",
      borderRadius: 20,
      padding: "5px 12px",
      boxSizing: "border-box",
      position: "relative",
      boxShadow:
        "0 2px 10px 0 rgba(99,102,241,0.12), inset 0 1px 0 rgba(255,255,255,0.6)",
      textTransform: "uppercase",
      letterSpacing: "0.9px",
      whiteSpace: "nowrap",
    }}
  >
    <Handle type="target" position={Position.Top} style={TARGET_HANDLE} />
    <LuZap
      size={9}
      color="#6366f1"
      strokeWidth={2.5}
      style={{ flexShrink: 0 }}
    />
    {data.label}
    <LuChevronRight
      size={8}
      color="#818cf8"
      strokeWidth={2.5}
      style={{ flexShrink: 0 }}
    />
    <Handle type="source" position={Position.Bottom} style={SOURCE_HANDLE} />
  </div>
);

export const InfoNode = ({ data }) => (
  <div
    style={{
      width: data.width,
      background: "linear-gradient(135deg,#fffbeb 0%,#fef9e7 100%)",
      border: "1px solid #fde68a",
      borderLeft: "3px solid #f59e0b",
      borderRadius: "0 10px 10px 0",
      padding: "10px 16px",
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      boxSizing: "border-box",
      boxShadow: "0 2px 8px -2px rgba(245,158,11,0.12)",
    }}
  >
    <LuInfo
      size={13}
      color="#b45309"
      strokeWidth={2.2}
      style={{ flexShrink: 0, marginTop: 1 }}
    />
    <span
      style={{
        fontSize: 10,
        color: "#92400e",
        lineHeight: 1.65,
        fontWeight: 500,
      }}
    >
      {data.label}
    </span>
  </div>
);

export const ActionBtn = ({ data }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: data.width,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 5,
        fontSize: 8.5,
        fontWeight: 700,
        color: hov ? "#fff" : T.primary,
        background: hov
          ? `linear-gradient(135deg,${T.primary} 0%,#1a5c9e 100%)`
          : "#fff",
        border: `1.5px solid ${T.primary}`,
        borderRadius: 50,
        padding: "5px 10px",
        cursor: "pointer",
        transition:
          "color 0.2s ease, background 0.2s ease, transform 0.2s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s ease",
        boxSizing: "border-box",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        boxShadow: hov ? `0 6px 16px -3px ${T.primary}50` : "none",
        transform: hov ? "scale(1.04)" : "scale(1)",
      }}
    >
      {data.label}
      <LuArrowRight
        size={9}
        color={hov ? "#fff" : T.primary}
        strokeWidth={2.5}
      />
    </div>
  );
};

export const LegendNode = ({ data }) => (
  <div
    style={{
      width: data.width,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 18,
      padding: "7px 18px",
      background: "rgba(248,250,252,0.95)",
      border: "1px solid #e2e8f0",
      borderRadius: 50,
      boxSizing: "border-box",
      boxShadow: "0 1px 6px -2px rgba(0,0,0,0.06)",
    }}
  >
    {data.items.map((item, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: item.color,
            flexShrink: 0,
            boxShadow: `0 0 0 2px ${item.color}28`,
          }}
        />
        <span
          style={{
            fontSize: 8,
            fontWeight: 600,
            color: "#475569",
            letterSpacing: "0.4px",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
        >
          {item.label}
        </span>
      </div>
    ))}
  </div>
);

export const nodeTypes = {
  groupNode: GroupNode,
  cardNode: CardNode,
  infoNode: InfoNode,
  actionBtn: ActionBtn,
  legendNode: LegendNode,
  pillNode: PillNode,
};

const initialNodes = [
  {
    id: "setup-cargo",
    type: "groupNode",
    position: { x: 40, y: 0 },
    data: {
      title: "Create Cargo",
      sub: "Manage cargo orders. Click 'More Actions' to quickly generate contracts directly from this tab.",
      width: 270,
      height: 115,
      route: "/orders",
      color: C.teal.border,
    },
  },
  {
    id: "setup-vessel",
    type: "groupNode",
    position: { x: 390, y: 0 },
    data: {
      title: "Create Vessel",
      sub: "Manage active fleet profiles. Search, filter, and verify parameters to directly generate contracts.",
      width: 270,
      height: 115,
      route: "/vesselManager",
      color: C.coral.border,
    },
  },
  {
    id: "cargo-cargo-est",
    type: "cardNode",
    position: { x: 100, y: 185 },
    data: {
      title: "Create Cargo Contract",
      sub: "Go to More Actions → Create Contract from any cargo order.",
      color: C.teal,
      route: "/orders",
      width: 150,
    },
  },
  {
    id: "vessel-vessel-est",
    type: "cardNode",
    position: { x: 450, y: 185 },
    data: {
      title: "Create Vessel Contract",
      sub: "Go to More Actions → Create Contract from any vessel profile.",
      route: "/vesselManager",
      color: C.coral,
      width: 150,
    },
  },
  {
    id: "cargo-contract-tab",
    type: "groupNode",
    position: { x: 30, y: 340 },
    data: {
      title: "Cargo Contract Tab",
      sub: "View all cargo contracts with operation type, status & key dates. Create estimates via Cargo-Cargo or Vessel-Cargo wizards. Share, duplicate contracts & manage counterparties.",
      width: 290,
      height: 130,
      route: "/contracts?tab=1",
      color: C.teal.border,
    },
  },
  {
    id: "vessel-contract-tab",
    type: "groupNode",
    position: { x: 380, y: 340 },
    data: {
      title: "Vessel Contract Tab",
      sub: "Manage vessel-side chartering. Review charter terms, create Vessel-Vessel or Vessel-Cargo estimates, update vessel specs & share with team.",
      width: 290,
      height: 130,
      route: "/contracts?tab=2",
      color: C.coral.border,
    },
  },
  {
    id: "prefix-est-tab",
    type: "groupNode",
    position: { x: 100, y: 590 },
    data: {
      title: "Pre-Fix Estimate",
      sub: "Finalized estimates ready for operations. Open calculation views per contract type and share as Private or Common to the Operations module. (Contract Tab)",
      width: 480,
      height: 120,
      route: "/contracts?tab=3",
      color: C.amber.border,
    },
  },
  {
    id: "ops-tab",
    type: "groupNode",
    position: { x: 160, y: 790 },
    data: {
      title: "Operation Tab",
      sub: "Execute voyage tasks, track fuel consumption, and monitor active operational fixtures.",
      width: 360,
      height: 90,
      route: "/operations",
      color: C.green.border,
    },
  },
];

const tealEdge = {
  type: "bezier",
  animated: true,
  style: { stroke: C.teal.dot, strokeWidth: 1.6, strokeDasharray: "5 3" },
  markerEnd: { type: MarkerType.ArrowClosed, color: C.teal.dot },
};
const coralEdge = {
  type: "bezier",
  animated: true,
  style: { stroke: C.coral.dot, strokeWidth: 1.6, strokeDasharray: "5 3" },
  markerEnd: { type: MarkerType.ArrowClosed, color: C.coral.dot },
};
const mergeEdge = {
  type: "bezier",
  animated: true,
  style: {
    stroke: "#94a3b8",
    strokeWidth: 1.2,
    strokeDasharray: "3 3",
    opacity: 0.5,
  },
  markerEnd: undefined,
};
const mainEdge = {
  type: "bezier",
  animated: true,
  style: { stroke: T.primary, strokeWidth: 1.8 },
  markerEnd: { type: MarkerType.ArrowClosed, color: T.primary },
};

const initialEdges = [
  {
    id: "e-cargo-cargoest",
    source: "setup-cargo",
    target: "cargo-cargo-est",
    ...tealEdge,
  },
  {
    id: "e-vessel-vesselest",
    source: "setup-vessel",
    target: "vessel-vessel-est",
    ...coralEdge,
  },
  {
    id: "e-cargoest-cargotab",
    source: "cargo-cargo-est",
    target: "cargo-contract-tab",
    ...tealEdge,
  },
  {
    id: "e-vesselest-vesseltab",
    source: "vessel-vessel-est",
    target: "vessel-contract-tab",
    ...coralEdge,
  },
  {
    id: "e-cargotab-prefix",
    source: "cargo-contract-tab",
    target: "prefix-est-tab",
    ...mergeEdge,
  },
  {
    id: "e-vesseltab-prefix",
    source: "vessel-contract-tab",
    target: "prefix-est-tab",
    ...mergeEdge,
  },
  {
    id: "e-prefix-opstab",
    source: "prefix-est-tab",
    target: "ops-tab",
    ...mainEdge,
  },
];

export default function WorkflowDiagram({ goToRoute, onClose }) {
  const [nodes, , onNodesChange] = useNodesState(
    JSON.parse(JSON.stringify(initialNodes)),
  );
  const [edges, , onEdgesChange] = useEdgesState(
    JSON.parse(JSON.stringify(initialEdges)),
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 350);
    return () => clearTimeout(timer);
  }, []);

  const onNodeClick = (_, node) => {
    if (node.data?.route) {
      goToRoute(node.data.route);
      onClose();
    }
  };

  return (
    <div style={{ width: "100%", height: "75vh", position: "relative" }}>
      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          onNodeClick={onNodeClick}
          fitView
          fitViewOptions={{ padding: 0.15 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          style={{ background: "#f8fafc" }}
        >
          <Background variant="dots" color="#cbd5e1" gap={20} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      )}
    </div>
  );
}
